import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { v4 as uuidv4 } from 'uuid';
import InferenceGuide from "./InferenceGuide";
import RefinementControlPanel from "./RefinementControlPanel";
import { fetchInferenceResults, fetchDirectInferenceResults, sendExclusionPoints, sendInclusionPoints, sendInclusionExclutionPoints } from "../../utils/inferenceUtils";

import {
  saveSegmentData,
  getProjectClasses,
  saveProjectClasses,
  fetchImageDetails,
  getProjectSegmentAnnotation,
} from "../../utils/crudFunctions";

import ClassModal from "./ClassModal";
import LeftBar from "./LeftBar";
import RightBar from "./RightBar";
import SegmentClassModal from "./SegmentClassModal";
import KonvaStage from "./KonvaStage";
import ComplexitySlider from "./ComplexitySlider";
// TODO:
// need to fetch the sam infer result on hover we just send that one point where we are hovering for more than 2 second
// add slider for simplicity
// toggle between seeing points or just the mask 
// show result of infer in the direct inference mode also

function SegmentComponent({ setProjectName }) {
  const { userName, projectName, index } = useParams();

  const [currentIndex, setCurrentIndex] = useState(parseInt(index) - 1 || 0);

  const [image, setImage] = useState(null);
  const [loadingCurrent, setLoadingCurrent] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(true);
  const [imageData, setImageData] = useState({});
  const [classes, setClasses] = useState([]);
  const [showClassModal, setShowClassModal] = useState(false);
  const [newClassName, setNewClassName] = useState("");
  const [modalMode, setModalMode] = useState("add");
  const [tool, setTool] = useState("move");
  const [segments, setSegments] = useState([]);
  const [currentPoints, setCurrentPoints] = useState([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [showSegmentModal, setShowSegmentModal] = useState(false);
  const [segmentModalPosition, setSegmentModalPosition] = useState({
    x: 0,
    y: 0,
  });
  const [selectedSegmentForModal, setSelectedSegmentForModal] = useState(null);
  const [selectedClassForSegment, setSelectedClassForSegment] = useState({});
  const [isLocked, setIsLocked] = useState(false);
  const [allAnnotations, setAllAnnotations] = useState([]);
  const [datasetLength, setDatasetLength] = useState(() => {
    const savedLength = localStorage.getItem(`${projectName}_datasetLength`);
    return savedLength ? parseInt(savedLength) : 0;
  });

  const [showSegments, setShowSegments] = useState(true);
  const stageRef = useRef(null);
  const imgRef = useRef(null);
  const currentImageRef = useRef(null);
  const [inferenceData, setInferenceData] = useState([]);
  const [inferenceLoading, setInferenceLoading] = useState(false);
  const [directInferenceMode, setDirectInferenceMode] = useState(false);

  const [directInferenceLoading, setDirectInferenceLoading] = useState(false);

  const abortControllerRef = useRef(new AbortController());

  const navigate = useNavigate();

  // Replace individual dimension states with a single dimensions state
  const [dimensions, setDimensions] = useState({
    original: { width: 0, height: 0 },
    rendered: { width: 0, height: 0 },
    scale: 1,
  });

  // Direct Inference Points: include and exclude
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const [hoverTimer, setHoverTimer] = useState(null);
  const [hoverInferenceResult, setHoverInferenceResult] = useState(null);
  const [showMask, setShowMask] = useState(false);
  const [inferComplexity, setInferComplexity] = useState("50");
  const [lockedHoverSegment, setLockedHoverSegment] = useState(null);
  const [inclusionPoints, setInclusionPoints] = useState([]);
  const [exclusionPoints, setExclusionPoints] = useState([]);
  // useEffect(() => { console.log('segments>>>', segments) }, [segments])

  // Load images and dataset length
  const cleanupResources = () => {
    // Cleanup current image
    if (currentImageRef.current) {
      if (currentImageRef.current.src.startsWith('blob:')) {
        URL.revokeObjectURL(currentImageRef.current.src);
      }
      currentImageRef.current.onload = null;
      currentImageRef.current.onerror = null;
      currentImageRef.current = null;
    }
    // Abort pending requests
    abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();
  };

  const loadCurrentImage = useCallback(async () => {
    if (isNaN(currentIndex)) return;
    cleanupResources();
    setLoadingCurrent(true);

    try {
      const imageDetails = await fetchImageDetails(projectName, currentIndex);
      if (!imageDetails?.image) {
        throw new Error("No image data received");
      }
      // Clean up previous image if it exists
      if (currentImageRef.current) {
        if (currentImageRef.current.src.startsWith('blob:')) {
          URL.revokeObjectURL(currentImageRef.current.src);
        }
        currentImageRef.current.onload = null;
        currentImageRef.current.onerror = null;
      }

      const img = new window.Image();
      currentImageRef.current = img;
      const imageLoadPromise = new Promise((resolve, reject) => {
        img.onload = () => {
          if (img.naturalWidth > 0 && img.naturalHeight > 0) {
            resolve(img);
          } else {
            reject(new Error("Invalid image dimensions"));
          }
        };
        img.onerror = () => {
          reject(new Error("Failed to load image"));
        };
      });

      img.src = `data:image/jpeg;base64,${imageDetails.image}`;

      // Wait for image to load
      await imageLoadPromise;

      setImage(img);
      setImageData({
        id: imageDetails.id,
        name: imageDetails.filename,
        data: `data:image/jpeg;base64,${imageDetails.image}`
      });
      // Calculate dimensions
      const maxWidth = window.innerWidth - 260;
      const maxHeight = window.innerHeight - 110;
      const ratio = Math.min(
        maxWidth / img.naturalWidth,
        maxHeight / img.naturalHeight,
        1
      );
      console.log("Ratio", ratio)
      setDimensions({
        original: {
          width: img.naturalWidth,
          height: img.naturalHeight,
        },
        rendered: {
          width: Math.round(img.naturalWidth * ratio),
          height: Math.round(img.naturalHeight * ratio),
        },
        scale: ratio,
      });
    } catch (error) {
      console.error("Error loading image:", error);
      setImageLoaded(false);
    } finally {
      setLoadingCurrent(false);

    }
  }, [currentIndex, projectName]
  )
  useEffect(() => {
    loadCurrentImage();
  }, [loadCurrentImage]);

  useEffect(() => {
    return () => {
      cleanupResources();
      setImage(null); // Clear image state
    };
  }, [currentIndex]);

  // ----------------------end images ------------------



  // Load classes from database
  const loadClasses = useCallback(async () => {
    try {
      const backendClasses = await getProjectClasses(projectName);
      const sorted = backendClasses.sort((a, b) => a.index - b.index);
      console.log('sorted classes>', sorted)
      setClasses(sorted);
      // await saveClasses(backendClasses);
    } catch (error) {
      console.error("Failed to load classes:", error);
    }
  }, [projectName])

  useEffect(() => {
    loadClasses();
  }, [loadClasses]);

  // Separate effect only for syncing route param → parent state
  useEffect(() => {
    setProjectName(projectName);
  }, [projectName, setProjectName]);

  // Load annotations
  useEffect(() => {
    // console.log("Loading annotations for project:", projectName);
    getProjectSegmentAnnotation(projectName).then((saved) => {
      // console.log("Loaded annotations:", saved);
      if (saved.length > 0) {

        setAllAnnotations(saved);
      }
    });
  }, [projectName]);

  // useEffect(() => {
  //   const fetchAnnotations = async () => {
  //     console.log(`🚀 Fetching segment annotations for project: ${projectName}`);

  //     const start = performance.now(); // start timer
  //     try {
  //       const saved = await getProjectSegmentAnnotation(projectName); // actual API call

  //       const end = performance.now();
  //       const durationMs = end - start;
  //       const durationSec = (durationMs / 1000).toFixed(2);

  //       // approximate payload size (serialized JSON)
  //       const sizeKB = (JSON.stringify(saved).length / 1024).toFixed(2);

  //       console.log(
  //         `%c[Segment Annotation API] ${projectName}`,
  //         "color: #2563eb; font-weight: bold;"
  //       );
  //       console.log(`⏱️ Time Taken: ${durationSec}s`);
  //       console.log(`📦 Response Size: ${sizeKB} KB`);
  //       console.log(`🧩 Total Annotations: ${Array.isArray(saved) ? saved.length : "N/A"}`);

  //       if (Array.isArray(saved) && saved.length > 0) {
  //         setAllAnnotations(saved);
  //       }
  //     } catch (error) {
  //       console.error("❌ Error loading segment annotations:", error);
  //     }
  //   };

  //   fetchAnnotations();
  // }, [projectName]);




  // Dynamic resizing
  useEffect(() => {
    const updateDimensions = () => {
      if (imgRef.current && dimensions.original.width > 0) {
        const maxWidth = window.innerWidth - 260;
        const maxHeight = window.innerHeight - 110;
        const ratio = Math.min(
          maxWidth / dimensions.original.width,
          maxHeight / dimensions.original.height,
          1
        );

        setDimensions((prev) => ({
          ...prev,
          rendered: {
            width: Math.round(dimensions.original.width * ratio),
            height: Math.round(dimensions.original.height * ratio),
          },
          scale: ratio,
        }));
      }
    };

    // Initial measurement
    updateDimensions();

    // Set up resize observer
    const resizeObserver = new ResizeObserver(updateDimensions);
    if (imgRef.current) {
      resizeObserver.observe(imgRef.current);
    }

    // Set up window resize listener as fallback
    window.addEventListener("resize", updateDimensions);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateDimensions);
    };
  }, [currentIndex, dimensions.original]);

  function inferenceToSegments(inferenceData, classes) {
    return inferenceData.map((inf, idx) => {
      // If inf.class is missing or empty, assign a default class name
      let className = '';
      if (inf.class && typeof inf.class === 'string' && inf.class.trim() !== '') {
        className = inf.class.split(' ').slice(1).join(' '); // e.g., "67 cell phone" -> "cell phone"
      } else {
        className = 'Unlabeled'; // or any default you prefer
      }
      const matchedClass = classes.find(cls => cls.name === className || cls.id === className);

      return {
        id: uuidv4(),
        points: inf.points,
        label: matchedClass
          ? {
            color: matchedClass.color,
            id: matchedClass.id || matchedClass.name,
            index: matchedClass.index,
            name: matchedClass.name,
          }
          : {
            color: "#FF00FF", // fallback color
            id: className,
            index: -1,
            name: className,
          },
      };
    });
  }

  // Load existing segments for current image
  useEffect(() => {
    if (!image) return;
    // console.log("Loading segments for current image:", currentIndex, allAnnotations);
    const existingAnnotations = allAnnotations.find((ann) => {
      return ann.imageIndex === currentIndex;
    });

    if (existingAnnotations && existingAnnotations.segments) {
      // Keep points in original coordinates - no scaling needed here
      console.log('in existing ann')
      setSegments(existingAnnotations.segments);
    } else if (
      inferenceData.length > 0
    ) {
      console.log("Using inference data for segments:");
      setSegments(inferenceToSegments(inferenceData, classes));
      // } else if (hoverInferenceResult && hoverInferenceResult.length > 0) {
      //   console.log("Using hover inference data for segments:");
      //   setSegments(inferenceToSegments(hoverInferenceResult, classes));
      // }
    } else {
      console.log('setting empty segments')
      setSegments([]);
    }
  }, [currentIndex, image, allAnnotations, inferenceData, classes]);


  // Navigation and URL handling

  const goToIndex = useCallback((newIndex) => {
    if (newIndex < 0 || newIndex >= datasetLength) return;
    cleanupResources();
    setCurrentIndex(newIndex);
    navigate(`/project/${userName}/${projectName}/segment/${newIndex + 1}`, {
      replace: true,
    });
  }, [datasetLength, userName, projectName, navigate]);

  // Save segments to annotations
  const handleSaveNext = async () => {
    if (currentIndex >= datasetLength - 1) {
      const currentImageData = {
        imageName: imageData?.name || `image_${imageData.id}`,
        imageIndex: imageData?.id,
        classes: classes,
        segments: segments.map((segment) => ({
          id: segment.id,
          points: segment.points.map((point) => {
            // Ensure we're working with numbers
            const x = typeof point[0] === "number" ? point[0] : 0;
            const y = typeof point[1] === "number" ? point[1] : 0;
            return [x, y];
          }),
          label: segment.label || null,
        })),
      };
      // console.log("Current Image Data:", currentImageData);
      const success = await saveSegmentData(projectName, currentImageData);

      if (success) {
        // Update local state
        setAllAnnotations((prev) => {
          // Create a new array without the current image's data (if it exists)
          const otherAnnotations = prev.filter(
            (item) => item.imageIndex !== currentImageData.imageIndex
          );

          // If we have segments, add the updated data, otherwise don't include it
          if (segments.length > 0) {
            // console.log("Saving segments:", [...otherAnnotations, currentImageData]);
            return [...otherAnnotations, currentImageData];
          } else {
            // If no segments, remove this image from annotations
            // console.log("No segments to save, removing image from annotations.", otherAnnotations);
            return otherAnnotations;
          }
        });
      } else {
        alert("Failed to save segmentation data. Please try again.");
      }
      return;
    }

    // Similar logic for non-last images
    const currentImageData = {
      imageName: imageData?.name || `image_${imageData.id}`,
      imageIndex: imageData?.id,
      classes: classes,
      segments: segments.map((segment) => ({
        id: segment.id,
        points: segment.points.map((point) => {
          const x = typeof point[0] === "number" ? point[0] : 0;
          const y = typeof point[1] === "number" ? point[1] : 0;
          return [x, y];
        }),
        label: segment.label || null,
      })),
    };
    // console.log("Current Image Data:", currentImageData);
    const success = await saveSegmentData(projectName, currentImageData);

    if (success) {
      setAllAnnotations((prev) => {
        // Create a new array without the current image's data (if it exists)
        const otherAnnotations = prev.filter(
          (item) => item.imageIndex !== currentImageData.imageIndex
        );

        // If we have segments, add the updated data, otherwise don't include it
        if (segments.length > 0) {
          // console.log("Saving segments:", [...otherAnnotations, currentImageData]);
          return [...otherAnnotations, currentImageData];
        } else {
          // If no segments, remove this image from annotations
          // console.log("No segments to save, removing image from annotations.", otherAnnotations);
          return otherAnnotations;
        }
      });

      if (imageLoaded) {
        const nextIndex = currentIndex + 1;
        goToIndex(nextIndex);
      }
    } else {
      alert("Failed to save segmentation data. Please try again.");
    }
  };

  const handleNull = async () => {
    if (currentIndex >= datasetLength - 1) return;

    const currentImageData = {
      imageName: imageData?.name || `image_${currentIndex}`,
      imageIndex: currentIndex,
      classes: classes,
      segments: null,
    };

    const success = await saveSegmentData(projectName, currentImageData);
    if (success) {
      setAllAnnotations((prev) => {
        const updated = [...prev];
        const existingIndex = updated.findIndex(
          (item) => item.imageIndex === currentImageData.imageIndex
        );
        const { classes, ...imageDataWithoutClasses } = currentImageData;
        if (existingIndex !== -1) {
          updated[existingIndex] = imageDataWithoutClasses;
        } else {
          updated.push(imageDataWithoutClasses);
        }
        return updated;
      });

      if (imageLoaded) {
        const nextIndex = currentIndex + 1;
        goToIndex(nextIndex);
      }
    }
    else {
      alert("Failed to save segmentation data. Please try again.");
    }
  };

  const handleNext = () => {
    if (currentIndex >= datasetLength - 1) return;
    const nextIndex = currentIndex + 1;
    goToIndex(nextIndex);
  };

  const handlePrev = () => {
    if (currentIndex <= 0) return;
    const prevIndex = currentIndex - 1;
    goToIndex(prevIndex);
  };

  // Zoom functionality-----------------------------------------
  // const handleZoom = (direction) => {
  //     const stage = stageRef.current;
  //     const scaleBy = 1.1;
  //     const oldScale = stage.scaleX();
  //     const center = {
  //       x: stage.width() / 2,
  //       y: stage.height() / 2,
  //     };

  //     const newScale =
  //       direction === "in" ? oldScale * scaleBy : oldScale / scaleBy;
  //     setZoomLevel(newScale);

  //     stage.scale({ x: newScale, y: newScale });

  //     const mousePointTo = {
  //       x: (center.x - stage.x()) / oldScale,
  //       y: (center.y - stage.y()) / oldScale,
  //     };

  //     const newPos = {
  //       x: center.x - mousePointTo.x * newScale,
  //       y: center.y - mousePointTo.y * newScale,
  //     };

  //     stage.position(newPos);
  //     stage.batchDraw();
  //   };

  // Enhanced zoom functionality with cursor-based zooming
  const handleZoom = (direction, pointer) => {
    const stage = stageRef.current;
    if (!stage) return;

    const scaleBy = 1.1;
    const oldScale = stage.scaleX();
    const newScale = direction === "in" ? oldScale * scaleBy : oldScale / scaleBy;
    const clampedScale = Math.max(0.1, Math.min(newScale, 10));
    console.log('pointer', pointer)
    // Provide a fallback if pointer is not valid
    const center =
      pointer && typeof pointer.x === "number" && typeof pointer.y === "number"
        ? pointer
        : { x: stage.width() / 2, y: stage.height() / 2 };

    // Calculate logical point in the image that should stay under the cursor
    const mousePointTo = {
      x: (center.x - stage.x()) / oldScale,
      y: (center.y - stage.y()) / oldScale
    };

    // Move the stage so the scaling keeps the zoom focal point under the pointer
    const newPos = {
      x: center.x - mousePointTo.x * clampedScale,
      y: center.y - mousePointTo.y * clampedScale
    };

    stage.scale({ x: clampedScale, y: clampedScale });
    stage.position(newPos);
    setZoomLevel(clampedScale);
    stage.batchDraw();
  };




  const handleResetZoom = () => {
    const stage = stageRef.current;
    stage.scale({ x: 1, y: 1 });
    stage.position({ x: 0, y: 0 });
    stage.batchDraw();
    setZoomLevel(1);
  };

  // Select a segment
  const handleSegmentClick = (segmentId) => {
    if (tool !== "select" || isLocked) return;

    const segment = segments.find((s) => s.id === segmentId);
    if (!segment) return;

    setSelectedSegmentForModal(segment);
    setSelectedClassForSegment(segment.label || "");

    // Calculate modal position (center of segment)
    const centerX =
      segment.points.reduce((sum, p) => sum + p.x, 0) / segment.points.length;
    const centerY =
      segment.points.reduce((sum, p) => sum + p.y, 0) / segment.points.length;

    setSegmentModalPosition({ x: centerX, y: centerY });
    setShowSegmentModal(true);
  };

  // Delete a segment
  const deleteSegment = (segmentId) => {
    setSegments(segments.filter((segment) => segment.id !== segmentId));
  };

  // Delete all segment
  const deleteAllSegment = (segmentId) => {
    setSegments([]);
  };

  // Handle class creation
  const handleSubmit = async () => {
    const name = newClassName.trim();
    if (!name) return;

    if (classes.some((cls) => cls.name === name)) {
      console.error("Class name must be unique.");
      return;
    }

    try {
      const newClasses = [...classes, { name }];
      setClasses(newClasses);
      setNewClassName("");
      // await saveClasses(newClasses);
      await saveProjectClasses(newClasses, projectName);
      await loadClasses();
    } catch (error) {
      console.error("Failed to add class:", error);
    }
  };

  const handleMagicInference = async () => {

    if (!projectName || imageData?.id == null) return;

    setInferenceLoading(true);
    try {
      const result = await fetchInferenceResults(projectName, imageData.id);
      // console.log('Inference result:', result);
      setInferenceData(Array.isArray(result.inference) ? result.inference : []);
    } catch (err) {
      console.log('infer error;', err);
      alert("Failed to fetch inference results");
    } finally {
      setInferenceLoading(false);
    }
  };



  // Direct Inference Handler (now only hover-based)
  const handleDirectInferenceSubmit = async () => {
    // No-op: direct inference is now handled by hover
    alert('Direct inference is now hover-based. Hover over the image for 2 seconds to run inference at a point.');
  };

  // Handler for hover in direct inference mode
  const handleStageHover = (point) => {
    if (!directInferenceMode || lockedHoverSegment) return;
    console.log('in handle stage hover');
    if (hoverTimer) clearTimeout(hoverTimer);
    setHoveredPoint(point);
    // setHoverInferenceResult(null);
    // Start timer for 2 seconds
    const timer = setTimeout(async () => {
      setDirectInferenceLoading(true);
      try {
        const result = await fetchDirectInferenceResults(projectName, imageData.id, point);
        const newPoints = result.inference?.[0].points || [];
        const newSegment = {
          id: uuidv4(),
          points: newPoints,
          label:
          {
            color: "#90E0EF", // fallback color
            id: null,
            index: -1,
            name: "",
          },
        }
        setHoverInferenceResult(newSegment || {});
        // setHoverInferenceResult(prev => [...(prev || []), ...(Array.isArray(result.inference) ? result.inference : [])]);
        console.log('Ran Hover Inference at Point:', point);
      } catch (err) {
        setHoverInferenceResult(null);
      } finally {
        setDirectInferenceLoading(false);
      }
    }, 1000);
    setHoverTimer(timer);
  };

  // Cleanup timer on mouse leave
  const handleStageMouseLeave = () => {
    if (hoverTimer) clearTimeout(hoverTimer);
    setHoveredPoint(null);
    // if (!lockedHoverSegment) setHoverInferenceResult(null);
  };

  const [pointHistory, setPointHistory] = useState({
    inclusion: [[]],
    exclusion: [[]],
    currentIndex: 0
  });
  const [batchMode, setBatchMode] = useState(false);
  const [pendingPoints, setPendingPoints] = useState({
    inclusion: [],
    exclusion: []
  });

  // Common handler for inclusion/exclusion points
  // Replace the existing handleInclusionExclusionPoints with this enhanced version
  const handleInclusionExclusionPoints = async (newInclusionPoints, newExclusionPoints, skipUpdate = false) => {
    setInclusionPoints(newInclusionPoints);
    setExclusionPoints(newExclusionPoints);

    // Update history
    setPointHistory(prev => ({
      inclusion: [...prev.inclusion.slice(0, prev.currentIndex + 1), newInclusionPoints],
      exclusion: [...prev.exclusion.slice(0, prev.currentIndex + 1), newExclusionPoints],
      currentIndex: prev.currentIndex + 1
    }));

    if (batchMode || skipUpdate) {
      setPendingPoints({ inclusion: newInclusionPoints, exclusion: newExclusionPoints });
      return;
    }

    try {
      const result = await sendInclusionExclutionPoints(
        projectName,
        imageData.id,
        newInclusionPoints,
        newExclusionPoints
      );

      const newPoints = result.inference?.[0].points || [];
      if (lockedHoverSegment && newPoints.length > 0) {
        const newSegment = {
          id: uuidv4(),
          points: newPoints,
          label: {
            color: "#90E0EF",
            id: null,
            index: -1,
            name: "",
          },
        };
        setHoverInferenceResult(newSegment || {});
        setSegments(segments.filter(seg => seg.id !== lockedHoverSegment.id));
      }
    } catch (err) {
      console.error("Failed to send inclusion/exclusion points", err);
    }
  };

  // Add undo/redo functions
  const undoPoints = () => {
    if (pointHistory.currentIndex > 0) {
      const newIndex = pointHistory.currentIndex - 1;
      setPointHistory(prev => ({ ...prev, currentIndex: newIndex }));
      const newInclusion = pointHistory.inclusion[newIndex];
      const newExclusion = pointHistory.exclusion[newIndex];
      handleInclusionExclusionPoints(newInclusion, newExclusion, true);
    }
  };

  const redoPoints = () => {
    if (pointHistory.currentIndex < pointHistory.inclusion.length - 1) {
      const newIndex = pointHistory.currentIndex + 1;
      setPointHistory(prev => ({ ...prev, currentIndex: newIndex }));
      const newInclusion = pointHistory.inclusion[newIndex];
      const newExclusion = pointHistory.exclusion[newIndex];
      handleInclusionExclusionPoints(newInclusion, newExclusion, true);
    }
  };

  // Batch processing function
  const submitBatchPoints = async () => {
    setBatchMode(false);
    await handleInclusionExclusionPoints(pendingPoints.inclusion, pendingPoints.exclusion);
    setPendingPoints({ inclusion: [], exclusion: [] });
  };



  if (!imageData?.data) {
    return (
      <div className="max-w-md mx-auto p-6 text-center text-gray-500">
        {loadingCurrent ? 'Loading image...' : 'No image to display'}
      </div>
    );
  }
  return (
    <div className="flex w-screen fixed h-[calc(100vh-3.5rem)] mt-14">
      <InferenceGuide
        mode={directInferenceMode ? 'directInference' : tool}
        lockedHoverSegment={lockedHoverSegment}
        inclusionPoints={inclusionPoints}
        exclusionPoints={exclusionPoints}
      />

      <RefinementControlPanel
        lockedHoverSegment={lockedHoverSegment}
        inclusionPoints={inclusionPoints}
        exclusionPoints={exclusionPoints}
        batchMode={batchMode}
        setBatchMode={setBatchMode}
        undoPoints={undoPoints}
        redoPoints={redoPoints}
        submitBatchPoints={submitBatchPoints}
        pointHistory={pointHistory}
        inferComplexity={inferComplexity}
        setInferComplexity={setInferComplexity}
        onUnlock={() => {
          setLockedHoverSegment(null);
          setHoverInferenceResult(null);
          setDirectInferenceMode(true);
          setInclusionPoints([]);
          setExclusionPoints([]);
        }}
        onFinishRefinement={() => {
          if (!hoverInferenceResult) return;

          setSegments(prev => {
            // Remove the locked segment if it exists and add the refined result
            const filteredSegments = prev.filter(seg => seg.id !== lockedHoverSegment?.id);
            return [...filteredSegments, hoverInferenceResult];
          });

          setSelectedSegmentForModal(hoverInferenceResult);
          setShowSegmentModal(true);
          setInclusionPoints([]);
          setExclusionPoints([]);
        }}

      />



      {/* Left Sidebar */}
      <LeftBar
        classes={classes}
        setClasses={setClasses}
        setShowClassModal={setShowClassModal}
        setModalMode={setModalMode}
        setNewClassName={setNewClassName}
        setSegments={setSegments}
        projectName={projectName}
        userName={userName}
        segments={segments}
        selectedSegmentForModal={selectedSegmentForModal}
        setSelectedSegmentForModal={setSelectedSegmentForModal}
        setShowSegmentModal={setShowSegmentModal}
        setSegmentModalPosition={setSegmentModalPosition}
        stageRef={stageRef}
        scale={dimensions.scale}
        onMagic={handleMagicInference}
        inferenceLoading={inferenceLoading}

        handleDirectInferenceSubmit={handleDirectInferenceSubmit}
        directInferenceLoading={directInferenceLoading}
      />

      <div className="flex-1 flex flex-col overflow-hidden h-full">
        {/* img part */}
        {/* Loading overlay - appears on top of existing content */}
        {loadingCurrent && (
          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 z-50">
            <div className="bg-white p-4 rounded-lg shadow-lg flex items-center">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Loading Image {currentIndex + 1}...
            </div>
          </div>
        )}
        <div className=" flex-1 flex justify-center items-center min-h-0 w-full h-full">
          <div
            className="relative  mx-auto h-full w-full"
            style={{
              width: "100%",
              height: "100%",
              padding: 0,
              margin: 0,
            }}
          >
            {/* Conditional rendering for direct inference mode */}
            <KonvaStage
              tool={tool}
              segments={segments}
              setHoverInferenceResult={setHoverInferenceResult}
              setIsDrawing={setIsDrawing}
              imgRef={imgRef}
              image={image}
              stageRef={stageRef}
              dimensions={dimensions}
              setSegments={setSegments}
              isDrawing={isDrawing}
              selectedSegmentForModal={selectedSegmentForModal}
              setSelectedSegmentForModal={setSelectedSegmentForModal}
              setSegmentModalPosition={setSegmentModalPosition}
              setShowSegmentModal={setShowSegmentModal}
              isLocked={isLocked}
              currentPoints={currentPoints}
              setCurrentPoints={setCurrentPoints}
              directInferenceMode={directInferenceMode}
              setShowSegments={setShowSegments}
              showSegments={showSegments}
              onHover={handleStageHover}
              onMouseLeave={handleStageMouseLeave}
              hoverInferenceResult={hoverInferenceResult}
              setDirectInferenceMode={setDirectInferenceMode}
              showMask={showMask}
              setShowMask={setShowMask}
              lockedHoverSegment={lockedHoverSegment}
              setLockedHoverSegment={setLockedHoverSegment}
              inclusionPoints={inclusionPoints}
              setInclusionPoints={setInclusionPoints}
              exclusionPoints={exclusionPoints}
              setExclusionPoints={setExclusionPoints}
              onAddInclusionPoint={async (pointsArr) => {
                handleInclusionExclusionPoints(pointsArr, exclusionPoints);
              }}
              onAddExclusionPoint={async (pointsArr) => {
                handleInclusionExclusionPoints(inclusionPoints, pointsArr);
              }}
              undoPoints={undoPoints}
              redoPoints={redoPoints}
              setBatchMode={setBatchMode}
              batchMode={batchMode}
              submitBatchPoints={submitBatchPoints}
              onZoom={handleZoom}
            />
          </div>
        </div>
        <div className="flex-shrink-0 border-t ">
          <div className="flex items-center justify-between p-1 border-t border-gray-200">
            <button
              onClick={handlePrev}
              className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
            >
              <FiChevronLeft className="mr-1" />
              Previous
            </button>

            <div className="flex flex-col items-center justify-center">
              <span className="text-sm font-medium text-gray-600">
                Image {currentIndex + 1} of {datasetLength}
              </span>
              <h3 className="text-sm font-medium text-gray-600">
                {imageData?.name || `Image ${currentIndex + 1}`}
              </h3>
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleNext}
                className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
              >
                Next
                <FiChevronRight className="ml-1" />
              </button>
              <button
                onClick={handleSaveNext}
                className="flex items-center px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors"
              >
                Save & Next
                <FiChevronRight className="ml-1" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Right Sidebar - Tools */}
      <RightBar
        setTool={setTool}
        tool={tool}
        zoomLevel={zoomLevel}
        handleZoom={handleZoom}
        handleResetZoom={handleResetZoom}
        segments={segments}
        setSegments={setSegments}
        selectedSegmentForModal={selectedSegmentForModal}
        setSelectedSegmentForModal={setSelectedSegmentForModal}
        setIsLocked={setIsLocked}
        isLocked={isLocked}
        handleNull={handleNull}
        deleteSegment={deleteSegment}
        setShowSegments={setShowSegments}
        showSegments={showSegments}
        deleteAllSegment={deleteAllSegment}
        setShowSegmentModal={setShowSegmentModal}
        setDirectInferenceMode={setDirectInferenceMode}
        directInferenceMode={directInferenceMode}
        showMask={showMask}
        setShowMask={setShowMask}
      />

      {/* Class Modal */}
      {showClassModal && (
        <ClassModal
          modalMode={modalMode}
          newClassName={newClassName}
          setNewClassName={setNewClassName}
          setShowClassModal={setShowClassModal}
          handleSubmit={handleSubmit}
        />
      )}

      {/* Segment Class Modal */}
      {showSegmentModal && (
        <SegmentClassModal
          position={segmentModalPosition}
          classes={classes}
          selectedClass={selectedClassForSegment}
          setSelectedClass={setSelectedClassForSegment}
          onClose={() => setShowSegmentModal(false)}
          onSave={(selectedClass) => {
            setSegments((prev) =>
              prev.map((segment) =>
                segment.id === selectedSegmentForModal.id
                  ? { ...segment, label: selectedClass }
                  : segment
              )
            );
            // Clean up refinement state
            setLockedHoverSegment(null);
            setDirectInferenceMode(true);
            setInclusionPoints([]);
            setExclusionPoints([]);
            setHoverInferenceResult(null);
            setShowSegmentModal(false);
          }}
        />
      )}


      {/* {directInferenceMode && <ComplexitySlider inferComplexity={inferComplexity} setInferComplexity={setInferComplexity} />} */}
    </div>
  );
}

export default SegmentComponent;


